import TableCommonHook from '@op/components/TableCommonHook';
import React, { useCallback, useMemo } from 'react';
import { ByKeyMetricsType } from '../constants';
import { channelInfo } from '@op/components/ChannelLabel';
import { transLanguage } from '@op/utils';
import { reviewsFormat } from '@op/utils/value';
import { useIntl } from 'umi';
import { TipsMapType } from '@op/components/BubbleCommon';

export default function Index({
  data,
  columns = [],
  byType,
  formatTimeFunc,
  formatValue = reviewsFormat,
  title,
  eventsDict = {},
  tableWrapper = (element: React.ReactNode) => element,
  detailPage = false,
}: {
  columns?: Array<any>;
  data?: Array<any>;
  byType: ByKeyMetricsType;
  formatTimeFunc?: (value: string) => string;
  formatValue?: (value: number) => string | number;
  title?: string;
  eventsDict?: { [key: string]: any };
  tableWrapper?: (
    element: React.ReactNode,
    tips?: TipsMapType,
    options?: { [key: string]: any },
  ) => React.ReactNode;
  detailPage?: boolean;
}) {
  const { formatMessage } = useIntl();
  const { onTableCellClick, ...tableEvents } = eventsDict || {};

  const handleCellClick = useCallback(
    (record: any, columnKey?: string) => {
      if (!detailPage || !onTableCellClick) return;
      onTableCellClick(record, columnKey);
    },
    [detailPage, onTableCellClick],
  );

  const resolveHeader = useCallback(
    (item: any): React.ReactNode => {
      if (byType === 'by_language' && item.header !== 'Others') {
        return transLanguage(item.key);
      }
      if (byType === 'total') {
        return formatMessage({ id: title || '' });
      }
      if (byType === 'by_channel' && item.header !== 'Others') {
        const { img, name } = channelInfo(item.key as any);
        return (
          <div className="dbo-inlineflex">
            <img src={img} style={{ marginRight: '3px' }} alt="" />
            {name}
          </div>
        );
      }
      return formatMessage({ id: item.header });
    },
    [byType, formatMessage, tableWrapper, title],
  );

  const tableColumns = useMemo(() => {
    const allColumns: Array<any> = columns.map((item) => ({
      header: resolveHeader(item),
      key: item.key,
      alight: 'right',
      width: 160,
      render: (cvm: any) => {
        const content = formatValue(cvm[item.key]);
        if (!detailPage || !onTableCellClick) {
          return content;
        }
        return tableWrapper(
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleCellClick(cvm, item.key);
            }}
          >
            {content}
          </div>,
        );
      },
    }));

    return [
      {
        header: formatMessage({ id: 'Date' }),
        key: 'time',
        align: 'left',
        width: 130,
        fixed: 'left',
        render: ({ time }: any) => {
          return tableWrapper(formatTimeFunc?.(time) || time);
        },
      },
      ...allColumns,
    ];
  }, [
    columns,
    detailPage,
    formatMessage,
    formatTimeFunc,
    formatValue,
    handleCellClick,
    onTableCellClick,
    resolveHeader,
    tableWrapper,
  ]);

  return (
    <TableCommonHook
      recordKey={'time'}
      columns={tableColumns}
      records={data}
      hasPageable={false}
      maxHeight={316}
      bordered
      minWidth={'auto'}
      {...tableEvents}
    ></TableCommonHook>
  );
}
